import {
    Typography,
    Button,
    Grid2 as Grid,
    Stack,
    Select,
    MenuItem,
    OutlinedInput,
    InputAdornment,
    Avatar,
    CircularProgress,
    ListItemIcon,
    SelectChangeEvent,
} from "@mui/material";
import React from "react";
import DashboardLayout from "../../../../Layouts/Dashboard/dashboard";
import BoxPaper from "../../../../Components/Misc/BoxPaper";
import ProvidersTable, { ExportHandle } from "../../../../Components/Table/ProvidersTable/providers-table";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import { routePaths } from "../../../../Router/router";
import MoreMenu from "../../../../Components/Misc/MoreMenu";
import CustomerUsersPanel from "../../../../Components/Panels/CustomerUsersPanel/customer-users-panel";
import { useToggle } from "../../../../Utils/Hooks/useToggle";
import SinglePageHeader from "../../../../Components/Misc/SinglePageHeader";
import { ingestService, userService } from "../../../../Core/Api/services";
import { DbAssetObjectInfos } from "../../../../Core/Api/Types/db-asset-object-infos";
import { DbUserBasicInfos } from "../../../../Core/Api/Types/db-user-basic-infos";
import { useParams } from "react-router";
import moment from "moment";
import MetadataListTextItem from "../../../../Components/Misc/Metadata/MetadataListTextItem/metadata-list-text-item";
import { DbLabProviderInfos } from "../../../../Core/Api/Types/db-lab-provider-infos";
import { RejectionRating } from "../../../../Core/Api/Types/rejection-rating";
import TableCellProgressBar from "../../../../Components/Table/TableCellProgressBar";
import ProviderEditPanel from "../../../../Components/Panels/ProviderEdit/provider-edit";
import { ObjectStatus } from "../../../../Core/Api/Types/object-status";
import GlobalConst from "../../../../Core/Utils/Constants/globals";
import { ModalContext } from "../../../../Components/Modals";
import ModalAddUser, { ModalAddUserElementProps } from "../../../../Components/Modals/ModalAddUser/modal-add-user";
import { UserHasAcl } from "../../../../Utils/helpers";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import ListPageHeader, {
    ListPageHeaderGroupActions,
    ListPageHeaderRight,
} from "../../../../Components/Misc/ListPageHeader";
import { useTranslation } from "react-i18next";

function ProvidersSingle() {
    const { t } = useTranslation();
    const { active: customerUsersPanel, toggle: toggleCustomerUsersPanel } = useToggle();
    const { active: providerEditPanelOpen, toggle: toggleProviderEditPanelOpen } = useToggle();
    const params = useParams();
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [assets, setAssets] = React.useState<DbAssetObjectInfos[]>([]);
    const [users, setUsers] = React.useState<DbUserBasicInfos[]>([]);
    const [provider, setProvider] = React.useState<DbLabProviderInfos>();
    const [rating, setRating] = React.useState<RejectionRating>();
    const [searchText, setSearchText] = React.useState<string>("");
    const [statuses, setStatuses] = React.useState<string[]>([]);
    const [filterStatus, setFilterStatus] = React.useState<string>("Default");
    const [rows, setRows] = React.useState<
        {
            title: string;
            type?: string;
            desc?: string;
            id: string;
            assetId: string;
            status: string;
            update: string;
            path?: string[];
        }[]
    >([]);

    const modalContext = React.useContext(ModalContext);
    const { openModal } = { ...modalContext };

    const handleNewUserClick = React.useCallback(() => {
        if (!openModal) {
            return;
        }

        const onClose: ModalAddUserElementProps["onClose"] = (data) => { };

        openModal<ModalAddUserElementProps>(ModalAddUser, { onClose });
    }, [openModal]);

    /*  const handleDeleteClick = (id: number) => {
      userService.DeleteUser(id);
    };
  */

    React.useEffect(() => {
        setStatuses(Object.values(ObjectStatus));
    }, []);

    React.useEffect(() => {
        if (params.id) {
            const getProvider = ingestService.GetProviderById(parseInt(params.id));
            const rejections = ingestService.GetRejectionsByProviderId(parseInt(params.id));
            const users = userService.GetUsersListByProviderId(parseInt(params.id));
            const ratings = ingestService.GetRejectionsRatings();
            Promise.all([getProvider, rejections, users, ratings]).then(
                ([providerResponse, rejectionsResponse, usersResponse, ratingsResponse]) => {
                    setIsLoading(false);
                    if (providerResponse.provider !== null) {
                        setProvider(providerResponse.provider);
                        if (ratingsResponse.ratings) {
                            setRating(
                                ratingsResponse.ratings?.find((itmInner) => itmInner.labProviderId === providerResponse.provider?.id)
                            );
                        }
                    }
                    if (rejectionsResponse.assets !== null) {
                        let rows = rejectionsResponse.assets.map((asset) => ({
                            id: String(asset.id),
                            assetId: String(asset.id),
                            title: asset.parentTitleName || "-",
                            desc: asset.comment || "-",
                            update: asset.updateInfos
                                ? moment(asset.updateInfos[asset.updateInfos.length - 1].date)
                                    .format("DD/MM/YYYY HH:mm:ss")
                                    .toString()
                                : "-",
                            type: asset.type,
                            status: asset.status,
                        }));
                        setAssets(rejectionsResponse.assets);

                        setRows(rows);
                    }
                    if (usersResponse.users !== null) {
                        setUsers(usersResponse.users);
                    }
                }
            );
        }
    }, [params.id]);

    //export
    const childRef = React.useRef<ExportHandle>(null);
    const handleExportClick = () => {
        childRef.current?.export();
    };

    const handleFilterStatus = (event: SelectChangeEvent<string>) => {
        setFilterStatus(event.target.value as string);
    };

    const additionalFilters = (
        <Select
            displayEmpty
            IconComponent={ExpandMoreRoundedIcon}
            // startAdornment={<EqualizerIcon />}
            inputProps={{ "aria-label": "Without label" }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            value={filterStatus}
            onChange={(value) => handleFilterStatus(value)}
        >
            <MenuItem value={"Default"}>{t("common:status")}</MenuItem>
            {statuses.map((status, index) => (
                <MenuItem value={status} key={index}>
                    {status}
                </MenuItem>
            ))}
        </Select>
    )

    return (
        <DashboardLayout noScroll sxContent={{ px: 2 }}>
            <SinglePageHeader title={provider?.labName || GlobalConst.LOADING_TEXT} mb={3}>
                <Button onClick={toggleProviderEditPanelOpen} startIcon={<EditOutlinedIcon />}>
                    {t("common:editUpperCase")}
                </Button>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleExportClick}
                    startIcon={<FileDownloadOutlinedIcon />}
                >
                    {t("common:export")}
                </Button>
            </SinglePageHeader>

            {isLoading && (
                <CircularProgress
                    sx={{
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                    }}
                />
            )}
            {!isLoading && (
                <Stack direction="row" gap={4} sx={{ flex: "1 1 0", minHeight: 0 }}>
                    <Stack sx={{ flex: "1 1 75%", minHeight: 0 }}>
                        <ProvidersTable
                            additionalFilters={additionalFilters}
                            rows={rows.filter(
                                (x) =>
                                    x.title?.toLowerCase().includes(searchText.toLowerCase()) &&
                                    (filterStatus === "Default" || x.status === filterStatus)
                            )}
                            assets={assets}
                            ref={childRef}
                        />
                    </Stack>
                    <Stack sx={{ flex: "1 1 25%", minHeight: 0, overflowY: "auto" }}>
                        <BoxPaper title={t("common:summary")} mb={3}>
                            <Stack mb={3}>
                                <Typography variant="h6" color="text.tier" mb={2}>
                                    {t("panel:accordionTitle.globalInfos")}
                                </Typography>
                                <Stack spacing={1.5}>
                                    <MetadataListTextItem title={t("menu:title.fullName")}>
                                        {provider?.labName || "-"}
                                    </MetadataListTextItem>
                                    <MetadataListTextItem title={t("common:description")}>
                                        {provider?.labDescription || "-"}
                                    </MetadataListTextItem>
                                    <MetadataListTextItem title={t("menu:title.emailAddress")}>
                                        {provider?.labEmail || "-"}
                                    </MetadataListTextItem>
                                    <MetadataListTextItem title={t("menu:title.asperaHost")}>
                                        {provider?.asperaAccount?.asperaHost || "-"}
                                    </MetadataListTextItem>
                                    <MetadataListTextItem title={t("menu:title.asperaLogin")}>
                                        {provider?.asperaAccount?.asperaLogin || "-"}
                                    </MetadataListTextItem>
                                    <MetadataListTextItem title={t("menu:title.asperaPassword")}>
                                        {provider?.asperaAccount?.asperaPassword || "-"}
                                    </MetadataListTextItem>
                                </Stack>
                            </Stack>
                            <Stack mb={3}>
                                <Typography variant="h6" color="text.tier" mb={2}>
                                    {t("panel:accordionTitle.ratingsInfos")}
                                </Typography>
                                <Stack spacing={1.5}>
                                    {rating?.acceptedPercentage && (
                                        <MetadataListTextItem title={t("menu:title.acceptedRate")}>
                                            <TableCellProgressBar progress={rating?.acceptedPercentage} />
                                        </MetadataListTextItem>
                                    )}
                                    <MetadataListTextItem title={t("menu:title.requestCount")}>
                                        {rating?.requestsCount || "-"}
                                    </MetadataListTextItem>

                                    <MetadataListTextItem title={t("menu:title.rejectionsCount")}>
                                        {rating && rating?.requestsCount && rating?.rejectedPercentage
                                            ? Math.round((rating?.requestsCount * rating?.rejectedPercentage) / 100)
                                            : "-"}
                                    </MetadataListTextItem>
                                </Stack>
                            </Stack>
                        </BoxPaper>
                        <BoxPaper
                            mb={3}
                            title={t("page:boxPaper.title.activeUsers")}
                            rightContent={
                                <Button
                                    variant="outlined"
                                    size="small"
                                    color="primary"
                                    startIcon={<PersonAddAltOutlinedIcon />}
                                    onClick={handleNewUserClick}
                                    disabled={!UserHasAcl(AclTypes.aclBackofficeUserCreate)}
                                >
                                    {t("button:newUser")}
                                </Button>
                            }
                        >
                            {users &&
                                users.slice(0, 5).map((user) => (
                                    <ul key={user.id}>
                                        <Stack component="li" direction="row" alignItems="center" justifyContent="space-between">
                                            <Stack direction="row" alignItems="center">
                                                <Avatar sx={{ width: 32, height: 32 }}></Avatar>
                                                <Typography fontWeight={500} ml={1}>
                                                    {user.name}
                                                </Typography>
                                            </Stack>

                                            <MoreMenu>
                                                <MenuItem>
                                                    <ListItemIcon>
                                                        <EditOutlinedIcon fontSize="small" />
                                                    </ListItemIcon>
                                                    {t("menu:item.editUserInfo")}
                                                </MenuItem>
                                                <MenuItem disabled>
                                                    <ListItemIcon>
                                                        <DeleteOutlineOutlinedIcon fontSize="small" />
                                                    </ListItemIcon>
                                                    {t("common:remove")}
                                                </MenuItem>
                                                <MenuItem disabled>
                                                    <ListItemIcon>
                                                        <MoreHorizOutlinedIcon fontSize="small" />
                                                    </ListItemIcon>
                                                    {t("common:moreInfo")}
                                                </MenuItem>
                                            </MoreMenu>
                                        </Stack>
                                    </ul>
                                ))}
                            {users.length === 0 && (
                                <Typography color="text.tier" fontWeight={500} ml={1}>
                                    {t("page:body.noUsersToShow")}
                                </Typography>
                            )}

                            {users.length > 5 && (
                                <Button sx={{ mt: 3 }} onClick={toggleCustomerUsersPanel} fullWidth>
                                    {t("button:viewMore")}
                                </Button>
                            )}
                        </BoxPaper>
                        <BoxPaper title={t("common:activities")} mb={3}>
                            {/* <ResourceNotification resourceId={providerId} resourceKind={ActivityKind.Asset} /> */}
                            <Typography color="text.tier" fontWeight={500} ml={1}>
                                {t("page:body.noActivitiesToShow")}
                            </Typography>
                        </BoxPaper>
                    </Stack>
                </Stack>
            )}
            <CustomerUsersPanel
                open={customerUsersPanel}
                onClose={toggleCustomerUsersPanel}
                users={users}
                header={provider?.labName || ""}
            />
            <ProviderEditPanel provider={provider!} open={providerEditPanelOpen} onClose={toggleProviderEditPanelOpen} />
        </DashboardLayout>
    );
}

export default ProvidersSingle;
